import React, { useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
} from '@mui/material';

import { Page, useAppLayoutContextConsumer, snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useIsUserPermittedTo, useAccessControlContext } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';
import { useDeleteKioskAssetMutation, User_Role } from '@occc/graphql';

import { AssetImagePreview } from './components/asset-image-preview.component';
import { AssetActions } from './components/asset-actions';

import { useGetKioskAsset } from '../../hooks/use-get-kiosk-asset.hook';
import { routeCreator } from '../../touchpoint.router';
import {
  ASSET_DELETE_ERROR_MESSAGE,
  ASSET_DELETE_SUCCESS_MESSAGE,
} from '../../touchpoint.constants';

export const AssetsDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isUserPermittedToEditAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_EDIT);
  const isUserPermittedToDeleteAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_DELETE);
  const accessControlContext = useAccessControlContext();
  const isAdminUser = accessControlContext?.userRole === User_Role.Admin;

  const { loading, error, asset } = useGetKioskAsset({ id: id || '' });
  const [deleteKioskAsset, { loading: deleteLoading }] = useDeleteKioskAssetMutation();

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.assetsList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.assetsDetails(id || ''),
        label: 'Assets',
      },
    ],
    [id],
  );

  useAppLayoutContextConsumer(breadcrumbLinks);

  const handleEdit = useCallback(() => {
    if (!id) return;
    navigate(routeCreator.assetsUpdate(id));
  }, [id, navigate]);

  const handleDelete = useCallback(async () => {
    if (!id) return;

    await deleteKioskAsset({
      variables: { id },
      onCompleted: () => {
        snackbar.success(ASSET_DELETE_SUCCESS_MESSAGE);
        navigate(routeCreator.assetsList());
      },
      onError: () => {
        snackbar.error(ASSET_DELETE_ERROR_MESSAGE);
      },
      refetchQueries: ['kioskAssets'],
    });
  }, [id, deleteKioskAsset, navigate]);

  const handleBack = () => {
    navigate(routeCreator.assetsList());
  };

  if (loading) {
    return (
      <Page
        browserTabTitle='Asset Details'
        pageHeaderProps={{
          title: 'Asset Details',
          subtitle: 'Loading...',
        }}
      >
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (error || !asset) {
    return (
      <Page
        browserTabTitle='Asset Details'
        pageHeaderProps={{
          title: 'Asset Details',
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardHeader
            title={
              <Typography variant='body1' fontWeight={500}>
                Asset images
              </Typography>
            }
          />
          <CardContent>
            <Typography color='error'>{error?.message || 'Asset not found'}</Typography>
            <Button onClick={handleBack} sx={{ mt: 2 }}>
              Back to Assets
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      browserTabTitle={`${asset.name} - Asset Details`}
      pageHeaderProps={{
        title: asset.name,
        subtitle: asset.description,
      }}
      actions={
        <Stack direction='row' spacing={1}>
          <AssetActions
            onEdit={handleEdit}
            onDelete={handleDelete}
            canEdit={isUserPermittedToEditAsset}
            canDelete={isAdminUser && isUserPermittedToDeleteAsset}
            deleteLoading={deleteLoading}
          />
        </Stack>
      }
    >
      <Card>
        <CardHeader
          title={
            <Typography variant='body1' fontWeight={500}>
              Asset images
            </Typography>
          }
        />
        <CardContent sx={{ overflowX: 'auto' }}>
          <AssetImagePreview imageSet={asset.imageSet} />
        </CardContent>
      </Card>
    </Page>
  );
};
